import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './wysiwyg.module.css';

const svg = (children) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const TOOLS = [
  {
    command: 'bold',
    title: 'Bold',
    icon: svg(
      <>
        <path d="M7 5h6a3.5 3.5 0 0 1 0 7H7z" fill="currentColor" stroke="none" />
        <path d="M7 12h7a3.5 3.5 0 0 1 0 7H7z" fill="currentColor" stroke="none" />
      </>
    )
  },
  {
    command: 'italic',
    title: 'Italic',
    icon: svg(
      <>
        <line x1="19" y1="5" x2="11" y2="5" />
        <line x1="13" y1="19" x2="5" y2="19" />
        <line x1="15" y1="5" x2="9" y2="19" />
      </>
    )
  },
  {
    command: 'underline',
    title: 'Underline',
    icon: svg(
      <>
        <path d="M6 4v6a6 6 0 0 0 12 0V4" />
        <line x1="5" y1="20" x2="19" y2="20" />
      </>
    )
  },
  {
    command: 'insertUnorderedList',
    title: 'Bulleted list',
    icon: svg(
      <>
        <line x1="9" y1="6" x2="20" y2="6" />
        <line x1="9" y1="12" x2="20" y2="12" />
        <line x1="9" y1="18" x2="20" y2="18" />
        <circle cx="4.5" cy="6" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="4.5" cy="12" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="4.5" cy="18" r="1.1" fill="currentColor" stroke="none" />
      </>
    )
  },
  {
    command: 'insertOrderedList',
    title: 'Numbered list',
    icon: svg(
      <>
        <line x1="10" y1="6" x2="20" y2="6" />
        <line x1="10" y1="12" x2="20" y2="12" />
        <line x1="10" y1="18" x2="20" y2="18" />
        <path d="M4 4v4" />
        <path d="M3 11h2l-2 3h2" />
        <path d="M3 17h2v3H3" />
      </>
    )
  },
  {
    command: 'createLink',
    title: 'Add link',
    icon: svg(
      <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </>
    )
  },
  {
    command: 'removeFormat',
    title: 'Clear formatting',
    icon: svg(
      <>
        <path d="M5 5h11" />
        <path d="M11 5l-3 10" />
        <line x1="14" y1="15" x2="20" y2="21" />
        <line x1="20" y1="15" x2="14" y2="21" />
      </>
    )
  }
];

const isEmptyHtml = (html) => !html || html === '<br>' || html === '<div><br></div>';

export const Wysiwyg = ({
  error = '',
  label = '',
  input_id = '',
  className = '',
  disabled = false,
  placeholder = '',
  value = '',
  onInput = () => {},
  style = {},
  extra_tools = []
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (el && value !== el.innerHTML) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => {
    const el = ref.current;
    if (!el) return;
    const html = isEmptyHtml(el.innerHTML) ? '' : el.innerHTML;
    onInput(html);
  };

  const exec = (command) => {
    if (disabled) return;
    ref.current?.focus();
    if (command === 'createLink') {
      const url = window.prompt('Link URL');
      if (!url) return;
      document.execCommand('createLink', false, url);
    } else {
      document.execCommand(command, false, null);
    }
    emit();
  };

  return (
    <div className={styles.formGroup}>
      {label && <label htmlFor={input_id}>{label}</label>}

      <div className={cx(styles.editorWrapper, { [styles.disabled]: disabled }, className)}>
        {!disabled && (
          <div className={styles.toolbar}>
            {TOOLS.map((tool) => (
              <button
                key={tool.command}
                type="button"
                title={tool.title}
                aria-label={tool.title}
                className={styles.toolBtn}
                onMouseDown={(e) => {
                  e.preventDefault();
                  exec(tool.command);
                }}
              >
                {tool.icon}
              </button>
            ))}
            {extra_tools.map((tool) => (
              <button
                key={tool.key || tool.title}
                type="button"
                title={tool.title}
                aria-label={tool.title}
                disabled={tool.disabled}
                className={styles.toolBtn}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (!tool.disabled) tool.onClick?.();
                }}
              >
                {tool.icon}
              </button>
            ))}
          </div>
        )}

        <div
          id={input_id}
          ref={ref}
          className={styles.editor}
          style={style}
          contentEditable={!disabled}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
        />
      </div>

      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};

export default Wysiwyg;
